import React, { useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { ExternalLink, Clock, Boxes, Package } from "lucide-react";
import { ACTION_CONFIG } from "../../pages/firm/warehouse/WarehouseTaskDetailDialog";
import { IconTextButton } from "../ButtonComponents";

const STATUS_CONFIG: Record<string, string> = {
  CREATED: "Oluşturuldu",
  IN_PROGRESS: "Devam Ediyor",
  COMPLETED: "Tamamlandı",
  CANCELLED: "İptal",
  PENDING: "Bekliyor",
  PENDING_PACKAGING: "Paketleme Bekliyor",
  PICKING: "Toplanıyor",
  PACKAGING: "Paketleniyor",
  PAUSED: "Durduruldu",
  SHORTAGE_REPORTED: "Eksik Bildirildi",
  UNPICKED: "Toplama İptal Edildi",
  UNPACKED: "Paketleme İptal Edildi",
  PACKER_REJECTED: "Paketleyici Reddetti",
};

const PICKING_ACTIONS = new Set([
  "ASSIGNED",
  "PICKING_DONE",
  "PICKING_PAUSED",
  "RESUMED",
  "SHORTAGE_REPORTED",
  "UNPICKED",
]);

const PACKAGING_ACTIONS = new Set([
  "PACKAGING_START",
  "PACKAGING_DONE",
  "PACKAGING_PAUSED",
  "UNPACKED",
  "PACKER_REJECTED",
]);

export const StaffActionDialog = ({
  visible,
  onHide,
  user,
  actionType,
  logs = [],
  onOpenTaskDetail,
}: any) => {
  const currentUserId = user?.id ?? user?.userId;

  // -----------------------------
  // FILTER LOGS (CLEAN VERSION)
  // -----------------------------
  const filteredLogs = useMemo(() => {
    return logs.filter((log: any) => {
      if (log.userId !== currentUserId) return false;

      const type = log.actionType?.trim();

      if (actionType === "PICKING") return PICKING_ACTIONS.has(type);
      if (actionType === "PACKAGING") return PACKAGING_ACTIONS.has(type);

      return true;
    });
  }, [logs, currentUserId, actionType]);

  // -----------------------------
  // GROUP BY TASK
  // -----------------------------
  const groupedTasks = useMemo(() => {
    const map = new Map<number, any>();

    filteredLogs.forEach((log: any) => {
      const key = Number(log.taskId);

      if (!map.has(key)) {
        map.set(key, {
          taskId: key,
          task: log.task || null,
          logs: [],
        });
      }

      map.get(key).logs.push(log);
    });

    return Array.from(map.values());
  }, [filteredLogs]);

  const PRIORITY_LABELS: Record<number, string> = {
    1: "Düşük",
    2: "Normal",
    3: "Yüksek",
    4: "Acil",
    5: "Kritik",
  };

  const getPriorityLabel = (value: number) =>
    PRIORITY_LABELS[value] || `P${value}`;

  const formatDate = (date: string) =>
    new Date(date).toLocaleString("tr-TR");

  const getAction = (type: string) =>
    ACTION_CONFIG?.[type] || {
      label: type,
      color: "gray",
    };

  const getHeaderTitle = () => {
    if (actionType === "PICKING")
      return "Toplama Operasyon Geçmişi";
    if (actionType === "PACKAGING")
      return "Paketleme Operasyon Geçmişi";
    return "Personel Aktivite Detayı";
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={`${user?.name || "Personel"} - ${getHeaderTitle()}`}
      className="w-[95vw] sm:w-[85vw] md:w-[75vw] lg:w-[85vw]"
    >
      <div className="max-h-[70vh] overflow-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">

        {groupedTasks.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-10">
            Kayıt bulunamadı
          </div>
        )}

        {groupedTasks.map((group: any) => {
          const task = group.task;

          const orderNumber = task?.order?.orderNumber || "-";
          const priority = task?.priority ?? 0;
          const complexity = task?.complexityScore ?? 0;
          const itemCount = task?.items?.length ?? 0;
          const statusLabel =
            STATUS_CONFIG[task?.status] || task?.status || "-";

          const isPick = actionType === "PICKING";

          return (
            <div
              key={group.taskId}
              className="card rounded-xl p-4 space-y-3 hover:shadow-md transition-all"
            >
              {/* HEADER */}
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div
                    className="font-semibold truncate"
                    style={{ color: "var(--text)" }}
                  >
                    {orderNumber}
                  </div>

                  <div
                    className="text-xs flex flex-wrap gap-x-2 gap-y-1 mt-1 opacity-80"
                    style={{ color: "var(--text)" }}
                  >
                    <span>#{group.taskId}</span>
                    <span>•</span>
                    <span>{group.logs.length} işlem</span>
                    <span>•</span>
                    <span>{itemCount} ürün</span>
                    <span>•</span>
                    <span>{getPriorityLabel(priority)}</span>
                    <span>•</span>
                    <span>S{complexity}</span>
                    <span>•</span>
                    <span>{statusLabel}</span>
                  </div>
                </div>

  <IconTextButton
           onClick={() => onOpenTaskDetail(group.taskId)}
            icon={ExternalLink}
            className="   text-xs p-3  m-1 rounded-lg bg-transparent hover:bg-black/10 dark:hover:bg-white/10 transition-all flex items-center gap-1 shadow-none"
          >
            Detay
          </IconTextButton>
              
              </div>

              {/* LOG LIST */}
              <div className="space-y-2">
                {group.logs
                  .sort(
                    (a: any, b: any) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((log: any) => {
                    const action = getAction(log.actionType);

                    return (
                      <div
                        key={log.id}
                        className="flex justify-between items-start p-3 card2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                      >
                        <div className="min-w-0">
                          <div
                            className="text-sm font-medium flex items-center gap-2"
                            style={{ color: "var(--text)" }}
                          >
                            {isPick ? (
                              <Boxes size={14} />
                            ) : (
                              <Package size={14} />
                            )}
                            {action.label}
                          </div>

                        
                        </div>

                        <div
                          className="text-xs opacity-60 whitespace-nowrap flex items-center gap-1"
                          style={{ color: "var(--text)" }}
                        >
                          <Clock size={12} />
                          {formatDate(log.createdAt)}
                        </div> 
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};